import { useCallback, useEffect, useMemo } from 'react';
import { useFocusEffect, useLocalSearchParams, useNavigation, useRouter } from 'expo-router';
import { useIntl } from 'react-intl';

import { useResponsive } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';

import {
  type HeaderIconItems,
  useHeaderButtonOptions,
  useHeaderLeft,
} from '../../../../components';
import { handleHardwareBackButtonPress } from '../../../../navigation';
import { giftCardConfirmationHeaderButtonsMessages as messages } from './useGiftCardConfirmationHeaderButtons.messages';

// ─────────────────────────────────────────────────────────────────────────────

/**
 * A small hook to conditionally render custom navigation header buttons.
 *
 * NOTE: We use custom back button navigation since this screen can be
 *       accessed from the gift card orders screen, which is located in a
 *       different stack, so the default `goBack` function won't work
 *       as expected.
 */
export const useGiftCardConfirmationHeaderButtons = () => {
  // ─── Route Params ─────────────────────────────────────────────────────

  const { referrer } = useLocalSearchParams<{ referrer?: string }>();

  // ─── Navigation ───────────────────────────────────────────────────────

  const navigation = useNavigation();
  const router = useRouter();

  const isInStoreOrdersEnabled = useFeatureFlag('CELS-4174-in-store-loyalty-orders-enabled'); // prettier-ignore
  const isNavbarV2Enabled = useFeatureFlag('CELS-5440-order-navbar-v2-enabled');

  const shouldShowGiftCardOrdersBackButton = referrer === 'gift-card-orders';

  const { currentBreakpoint } = useResponsive();
  const { formatMessage } = useIntl();

  // ─── Helpers ─────────────────────────────────────────────────────────

  const navigateToGiftCardsScreen = useCallback(() => {
    router.dismissTo('/gift');
  }, [router]);

  const navigateToGiftCardOrdersScreen = useCallback(() => {
    if (isInStoreOrdersEnabled) {
      router.navigate('/account/gift-cards');
      return;
    }

    router.navigate({ pathname: '/account/orders', params: { tabId: 'gift-cards' } }); // prettier-ignore
  }, [isInStoreOrdersEnabled, router]);

  const navigateToPreviousScreen = useCallback(() => {
    if (shouldShowGiftCardOrdersBackButton) {
      navigateToGiftCardOrdersScreen();

      return;
    }

    navigateToGiftCardsScreen();
  }, [
    navigateToGiftCardOrdersScreen,
    navigateToGiftCardsScreen,
    shouldShowGiftCardOrdersBackButton,
  ]);

  const overrideHardwareBackButtonPress = useCallback(() => {
    return handleHardwareBackButtonPress(() => {
      navigateToPreviousScreen();

      return true;
    });
  }, [navigateToPreviousScreen]);

  // ─── Header Controls ─────────────────────────────────────────────────

  const headerLeftItems = useMemo<HeaderIconItems>(() => {
    return [
      {
        key: 'join-or-sign-in-screen-header-back-btn',
        icon: 'IconArrowLeft',
        accessibilityLabel: formatMessage(messages.backButtonA11yLabel),
        onPress: navigateToPreviousScreen,
      },
    ];
  }, [formatMessage, navigateToPreviousScreen]);

  const shouldHideControls = isNavbarV2Enabled || !currentBreakpoint.isXS;
  const headerLeftXS = useHeaderLeft({ items: headerLeftItems });
  const headerLeft = shouldHideControls ? renderNoHeaderLeft : headerLeftXS;
  const { headerLeftOptions } = useHeaderButtonOptions({ headerLeft });

  // ─── Effects ─────────────────────────────────────────────────────────

  useFocusEffect(overrideHardwareBackButtonPress);

  /**
   * Set header controls.
   */
  useEffect(() => {
    const setOptions = navigation.setOptions;

    setOptions({
      ...headerLeftOptions,

      // NOTE: We use a custom back button, thus we need to hide the default one.
      headerBackVisible: false,
    });
  }, [headerLeftOptions, navigation]);
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/**
 * A no-op headerLeft renderer that prevents the default `HeaderBackButton`
 * from mounting.
 */
function renderNoHeaderLeft() {
  return null;
}
